import i18n from "@/core/plugins/vue-i18n";
import jsBarcode from "jsbarcode";
import CryptoJS from "crypto-js";
import * as selection from "./selection.js";
import * as helper from "./helper.js";
import * as dateTimeUtil from "./dateTimeUtil.js";
import * as dataFormat from "./dataFormatHelper.js";
import Vue from "vue";

/**Data Formatting */
const newLineToBr = function (val) {
	if (val == null || val.toString().trim() == "") {
		return "";
	}

	return val.replaceAll(/\n/gi, "<br />");
};

const handleTrimString = function (v) {
	return emptyObjectValidation(v) ? "" : v.toString().trim();
};

const numberConvertExcelCol = function (number) {
	let result = "";
	do {
		const letter = String.fromCharCode(65 + (number % 26));
		result = letter + result;
		number = Math.floor(number / 26) - 1;
	} while (number >= 0);
	return result;
};

const joinStr = function (data, separator = ", ") {
	return data.join(separator);
};

/**Validation Utilities */
const emptyStringValidation = function (value, include_zero = 0) {
	return value == null ||
		value == "NaN" ||
		value.toString().trim() == "" ||
		(include_zero == 1 && parseFloat(value.toString()) == 0)
		? true
		: false;
};

const emptyObjectValidation = (value, include_zero = false) => {
	if (typeof value == "undefined" || value == null) return true;
	if (
		(typeof value === "string" || typeof value === "number") &&
		(value == "NaN" ||
			value.toString().trim() == "" ||
			(include_zero && parseFloat(value.toString()) == 0))
	) {
		return true;
	}
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") return Object.keys(value).length === 0;
	return false;
};

const numberValidation = (value) => {
	const num = parseFloat(String(value).replace(/,/g, ""));
	return !isNaN(num);
};

const viewPage = function (path) {
	return path.indexOf("detail") !== -1 || path.indexOf("view") !== -1 ? 1 : 0;
};

const createPage = function (path, name = "create") {
	return path.indexOf(name) !== -1 || path.indexOf("copy") !== -1 ? 1 : 0;
};

const copyPage = function (path, name = "copy") {
	return path.indexOf(name) !== -1 ? 1 : 0;
};

const editPage = function (path, name = "edit") {
	return path.indexOf(name) !== -1 ? true : false;
};

const resetSelectionValue = function (
	selection,
	current_value,
	field_name = "id",
) {
	return emptyObjectValidation(
		selection.filter((item) => item[field_name] == current_value),
	)
		? true
		: false;
};

const summaryPageRowActionValidation = function (data) {
	return !emptyObjectValidation(data) ? data[0] : {};
};

const resetSearchPageNum = function (tableRef) {
	if (
		!emptyObjectValidation(tableRef.options) &&
		!emptyObjectValidation(tableRef.options.page) &&
		tableRef.options.page != 1
	) {
		tableRef.options.page = 1;
	}
};

/**UI Utilities */
const handleStatusDesc = function (status, module = null) {
	const statusMap = {
		default: {
			1: i18n.t("COMMON.STATUS.ACTIVE"),
			0: i18n.t("COMMON.STATUS.INACTIVE"),
		},
		"do-status": {
			1: i18n.t("COMMON.STATUS.CLOSED"),
			0: i18n.t("COMMON.STATUS.OPEN"),
		},
		"loading-status": {
			1: i18n.t("COMMON.STATUS.CLOSED"),
			0: i18n.t("COMMON.STATUS.OPEN"),
		},
		specialProd: {
			3: i18n.t("COMMON.STATUS.UNVERIFIED"),
		},
		"request-account": {
			1: i18n.t("COMMON.STATUS.APPROVED"),
			2: i18n.t("COMMON.STATUS.REJECTED"),
			3: i18n.t("COMMON.STATUS.SUSPEND"),
			5: i18n.t("COMMON.STATUS.VERIFY"),
			0: i18n.t("COMMON.STATUS.PENDING"),
		},
		delivery_address: {
			3: i18n.t("COMMON.STATUS.SUSPEND"),
		},
		customer: {
			3: i18n.t("COMMON.STATUS.SUSPEND"),
		},
		epo: {
			0: i18n.t("EPO_MANAGEMENT.EPO_LISTING.TEXT_SAVE_AS_DRAFT"),
		},
		"consignment-so-status": {
			1: i18n.t("COMMON.STATUS.CONFIRM"),
			0: i18n.t("COMMON.STATUS.PENDING"),
		},
		payment_instruction_status: {
			100: i18n.t(
				"COMMON.STATUS.PENDING_ACKNOWLEDGE",
			),
			200: i18n.t(
				"COMMON.STATUS.ACKNOWLEDGED",
			),
			300: i18n.t(
				"COMMON.STATUS.REQUEST_REVERT",
			),
			400: i18n.t(
				"COMMON.STATUS.REVERTED",
			),
			500: i18n.t(
				"COMMON.STATUS.DONE",
			),
		},
	};

	return (
		statusMap[module]?.[status] ||
		statusMap.default[status] ||
		i18n.t("COMMON.STATUS.INACTIVE")
	);
};

const handleBadgeVariant = function (status, module = null) {
	const variantMap = {
		"request-account": {
			default: "warning",
			1: "success",
			2: "danger",
			3: "warning",
			5: "primary",
			0: "primary",
		},
		"loading-status": {
			1: "danger",
			0: "success",
		},
		"do-status": {
			1: "success",
			0: "danger",
		},
		delivery_address: {
			3: "warning",
		},
		customer: {
			3: "warning",
		},
		"consignment-so-status": {
			1: "success",
			0: "primary",
		},
		payment_instruction_status: {
			100: "info",
			200: "primary",
			300: "green",
			400: "warning",
			500: "success",
		},
		destination_range: {
			1: "primary",
			2: "success",
		},
		default: {
			1: "success",
			0: "danger",
		},
	};

	return variantMap[module]?.[status] || variantMap.default[status] || "danger";
};

const handleBadgeVariantTaxRebate = function (status) {
	switch (status) {
		case 0:
			return "primary";
		case 1:
			return "success";
		case 2:
			return "info";
		default:
			return "danger";
	}
};

const handleYesNo = function (value) {
	return value != null && value == 1
		? i18n.t("COMMON.STATUS.YES")
		: i18n.t("COMMON.STATUS.NO");
};

const handleYesNoOptional = function (value) {
	if (value === 0) {
		return i18n.t("COMMON.STATUS.NO");
	} else if (value === 1) {
		return i18n.t("COMMON.STATUS.REQUIRED");
	} else if (value === 2) {
		return i18n.t("COMMON.STATUS.OPTIONAL");
	} else {
		return i18n.t("COMMON.STATUS.UNKNOWN");
	}
};

const handleDialogBoxWidth = function () {
	let width = emptyObjectValidation(window.innerWidth)
		? 600
		: Math.floor(parseInt(window.innerWidth) * 0.5);
	return parseInt(width) == 0 ? `600px` : `${width}px`;
};

const dialogboxWidth = function (rate = 0.5) {
	let width = emptyObjectValidation(window.innerWidth)
		? 600
		: Math.floor(parseInt(window.innerWidth) * rate);
	return parseInt(width) == 0 ? `600px` : `${width}px`;
};

const dialogboxHeight = function (rate = 0.5) {
	let height = emptyObjectValidation(window.innerHeight)
		? 600
		: Math.floor(parseInt(window.innerHeight) * rate);
	return parseInt(height) == 0 ? `600px` : `${height}px`;
};

const handleRequiredMsg = function (field) {
	return i18n.t("COMMON.VALIDATION.REQUIRED", {
		name: field,
	});
};

const successMsgHandle = function (create_page, module_name) {
	let title =
		create_page == 1
			? "COMMON.MSG_SUCCESS_TO_CREATE"
			: "COMMON.MSG_SUCCESS_TO_UPDATE";
	return i18n.t(title, {
		mod: module_name,
	});
};

const failedMgsHandle = function (create_page, module_name) {
	let title =
		create_page == 1
			? "COMMON.MSG_FAILED_CREATE"
			: "COMMON.MSG_FAILED_TO_UPDATE";
	return i18n.t(title, {
		mod: module_name,
	});
};

const placeHolderHandle = function (field_name) {
	return i18n.t("COMMON.PH_ENTER_TEXTBOX", {
		txt_fieldname: field_name,
	});
};

const setRequiredErrorBorder = function (
	state,
	required = 0,
	vuetify_style = 0,
) {
	let required_border =
		required == 1
			? vuetify_style == 0
				? `border border-info`
				: `required-border`
			: ``;
	let error_border =
		vuetify_style == 0 ? `border border-danger` : `error-border`;
	return state != false ? required_border : error_border;
};

const handleSelectedRow = function (row) {
	return emptyObjectValidation(row) ? [] : Array.isArray(row) ? row : [row];
};

const generateBarcode = function (id, barcodeStr, options, className) {
	if (emptyObjectValidation(barcodeStr)) return;

	const defaultOptions = {
		fontSize: 30,
		height: 30,
		width: 2,
		displayValue: true,
	};
	const selector = className ? `.${className}` : `#${id}`;
	jsBarcode(selector, barcodeStr, { ...defaultOptions, ...options });
};

const isDarkColor = function (hexColor) {
	if (!hexColor || typeof hexColor !== "string") return false;
	hexColor = hexColor.replace("#", "");

	let r, g, b;
	if (hexColor.length === 3) {
		r = parseInt(hexColor[0] + hexColor[0], 16);
		g = parseInt(hexColor[1] + hexColor[1], 16);
		b = parseInt(hexColor[2] + hexColor[2], 16);
	} else {
		r = parseInt(hexColor.substring(0, 2), 16);
		g = parseInt(hexColor.substring(2, 4), 16);
		b = parseInt(hexColor.substring(4, 6), 16);
	}

	const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

	return brightness < 135;
};

const selectOption = function (data, custom_option) {
	let default_option = {
		with_select_option: false, //change to [false] will not return select option
		id_field_name: "id", //change id field name
		id_field_value: null, //change id field value
		text_field_name: "name", //change text field name
		text_field_value: i18n.t("COMMON.SELECTION_OPTION_TITLE"), //change text field value
		with_others_option: false, //change to [true] will return others option at last index
		others_id_field_value: 0, //change others option id field value
		others_text_field_value: i18n.t("COMMON.SELECTION_OTHERS"), //change others option text field value,
		other_option_upper: true,
		text_field_value_all: false,
		text_field_none: false,
	};
	let option = Object.assign({}, default_option, custom_option);

	if (option.text_field_value_all == true) {
		option.with_select_option = true;
		option.text_field_value = i18n.t("COMMON.TXT_ALL");
	}

	if (option.text_field_none == true) {
		option.with_select_option = true;
		option.text_field_value = i18n.t("COMMON.SELECTION_NONE");
	}

	let result = [];
	if (option.with_select_option === true) {
		let select_option_data = {};

		select_option_data[option.id_field_name] = option.id_field_value;
		select_option_data[option.text_field_name] = option.text_field_value;

		result = result.concat(select_option_data);
	}

	if (typeof data !== "undefined") {
		if (data) {
			if (data.length > 0) {
				result = result.concat(data);
			}
		}
	}

	if (option.with_others_option === true) {
		let others_option_data = {};

		others_option_data[option.id_field_name] = option.others_id_field_value;
		others_option_data[option.text_field_name] = option.other_option_upper
			? option.others_text_field_value.toUpperCase()
			: option.others_text_field_value;

		result = result.concat(others_option_data);
	}

	return result;
};

const disabledButton = function (ele) {
	let buttons = document.querySelectorAll(ele);
	buttons.forEach((btn) => {
		btn.classList.add("spinner", "spinner-light", "spinner-right");
		btn.disabled = true;
	});
};

const enableButton = function (ele) {
	let buttons = document.querySelectorAll(ele);
	buttons.forEach((btn) => {
		btn.classList.remove("spinner", "spinner-light", "spinner-right");
		btn.disabled = false;
	});
};

const textColor = function (hex) {
	if (hex == null) return "#000";
	let c = hex.substring(1).split("");
	if (c.length == 3) {
		c = [c[0], c[0], c[1], c[1], c[2], c[2]];
	}
	c = "0x" + c.join("");
	let rgb =
		"rgba(" + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") + ",1)";
	let a = rgb.match(
		/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
	),
		r = a[1],
		g = a[2],
		b = a[3];
	var hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
	return hsp > 170 ? "#000" : "#fff";
};

const colorIndicatorStyle = function (bgColor, textColor, boxType = 0) {
	if (bgColor == null) bgColor = "#fff";
	return {
		backgroundColor: bgColor,
		border: bgColor == "#FFFFFF" ? "solid 1px #c7c5c5" : "none",
		cursor: "pointer",
		height: boxType == 2 ? "20px" : "26px",
		"line-height": boxType == 2 ? "20px" : "26px",
		padding: "0 10px",
		color: textColor,
		"text-align": boxType == 0 ? "left" : "center",
		transition: "border-radius 200ms ease-in-out",
		"font-weight": "900",
		"margin-bottom": boxType == 2 ? "3px" : "0",
	};
};

/**Security Utilities */
const encrypData = function (data) {
	const secret = "E2OZyKHKBqhvY9FqLfaouSIA7zQCDLSM";
	const iv = "0000000000000000";

	var encrpyted = CryptoJS.AES.encrypt(data, CryptoJS.enc.Utf8.parse(secret), {
		iv: CryptoJS.enc.Utf8.parse(iv),
		mode: CryptoJS.mode.CBC,
		padding: CryptoJS.pad.Pkcs7,
	});

	return encrpyted.toString();
};

const encryJsonPostData = function (post_data) {
	return {
		form: btoa(encrypData(JSON.stringify(post_data))),
	};
};

const decryptData = function (encryptedBase64) {
	const secret = "E2OZyKHKBqhvY9FqLfaouSIA7zQCDLSM";
	const iv = "0000000000000000";

	// Decode from Base64 (since btoa was used on the encrypted result)
	const encryptedData = atob(encryptedBase64);

	// Decrypt
	const decrypted = CryptoJS.AES.decrypt(
		encryptedData,
		CryptoJS.enc.Utf8.parse(secret),
		{
			iv: CryptoJS.enc.Utf8.parse(iv),
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		},
	);

	// Convert to UTF-8 string
	return decrypted.toString(CryptoJS.enc.Utf8);
};

/**Math Utilities */
const roundNumber = function (number, decimal_place = 2) {
	if (number == null) number = 0;
	number = numberHandle(number);
	const multiplier = Math.pow(10, decimal_place);
	// return parseFloat(number).toFixed(decimal_place);
	return Math.round(number * multiplier) / multiplier;
};

const math = {
	add: (x, y, decimals = 2, numbers = []) => {
		if (
			!emptyObjectValidation(x) &&
			!emptyObjectValidation(y) &&
			emptyObjectValidation(numbers)
		) {
			return roundNumber(
				(numberHandle(x) || 0) + (numberHandle(y) || 0),
				decimals,
			);
		} else {
			return numbers.reduce((sum, n) => {
				return roundNumber(
					(numberHandle(n) || 0) + (numberHandle(sum) || 0),
					decimals,
				);
			}, parseFloat(0));
		}
	},
	subtract: (x, y, decimals = 2) =>
		roundNumber((numberHandle(x) || 0) - (numberHandle(y) || 0), decimals),
	multiply: (x, y, decimals = 2) =>
		roundNumber((numberHandle(x) || 0) * (numberHandle(y) || 0), decimals),
	divide: (x, y, decimals = 2) => {
		const divisor = numberHandle(y) || 1;
		return roundNumber((numberHandle(x) || 0) / divisor, decimals);
	},
};

const pixelToMm = function (pixel) {
	return Math.ceil(pixel * 0.2645833333);
};

const negativeNumber = function (event) {
	let keyCode = event.keyCode ? event.keyCode : event.which;
	if (
		(keyCode < 48 || keyCode > 57) &&
		keyCode !== 46 &&
		keyCode == 45 &&
		event.target.selectionStart !== 0
	) {
		// 46 is dot
		event.preventDefault();
	}
};

const sumArrayObj = function (arrObj, field, decimal_place = 2) {
	if (emptyObjectValidation(arrObj)) {
		return 0;
	}

	return arrObj.reduce((sum, a) => {
		return math.add(sum, numberHandle(a[field], decimal_place), decimal_place);
	}, parseFloat(0));
};

const numberHandle = function (value) {
	// Handle null/undefined/empty string
	if (emptyObjectValidation(value, true)) {
		return 0;
	}

	// If already a number, return it directly
	if (typeof value === "number") {
		return value;
	}

	// Convert to string and clean it
	const stringValue = String(value).trim();

	// Return 0 for empty string after trim
	if (stringValue === "") {
		return 0;
	}

	// Remove thousand separators
	const cleanValue = stringValue.replace(/,/g, "");

	// Parse to float
	const numberValue = parseFloat(cleanValue);

	// Return 0 for NaN, otherwise the parsed number
	return isNaN(numberValue) ? 0 : numberValue;
};

const onlyNumber = function (event) {
	let keyCode = event.keyCode ? event.keyCode : event.which;
	if ((keyCode < 48 || keyCode > 57) && keyCode !== 46 && keyCode !== 44) {
		// 46 is dot
		event.preventDefault();
	}
};

const onlyInteger = function (event) {
	let keyCode = event.keyCode ? event.keyCode : event.which;
	if (keyCode < 48 || keyCode > 57) {
		// 46 is dot
		event.preventDefault();
	}
};

// ==============================
// Helper Utilities
// ==============================
const handleRemoveDataTableSort = function (options) {
	options.sortBy = [];
	options.sortDesc = [];
};

const resetObjServer = function (obj) {
	if (emptyObjectValidation(obj)) return null;
	return JSON.parse(JSON.stringify(obj));
};

const contactNoFormatHandle = function (calling_code, contact_no) {
	return contact_no != null && contact_no.trim() != ``
		? `${calling_code}${contact_no}`
		: ``;
};

const soNoFullFormat = function (soNo, versionNo) {
	return `${soNo} - ${versionNo}`;
};

const unserializeJsonStr = function (value) {
	if (emptyObjectValidation(value)) {
		return value;
	}
	return JSON.parse(value);
};

const serializeJsonString = function (value) {
	if (emptyObjectValidation(value)) {
		value = {};
	}
	return JSON.stringify(value);
};

const convertNestedObjectToNull = function (
	object,
	maxLevel = 3,
	reset_field = {},
) {
	if (object == null || typeof object !== "object") return null;

	const processObject = (obj, currentLevel) => {
		if (currentLevel > maxLevel) return;

		for (const key in obj) {
			if (obj[key] != null) {
				if (typeof obj[key] === "object" && currentLevel < maxLevel) {
					processObject(obj[key], currentLevel + 1);
				} else {
					obj[key] = null;
				}
			}
		}
	};

	processObject(object, 1);

	if (!emptyObjectValidation(reset_field)) {
		for (const [key, value] of Object.entries(reset_field)) {
			object[key] = value;
		}
	}
	return object;
};

const handleArrayLastIndex = function (data) {
	return emptyObjectValidation(data) ? 0 : parseInt(data.length) - 1;
};

const countObjLen = function (data) {
	return parseInt(emptyObjectValidation(data) ? 0 : parseInt(data.length));
};

const resetState = function (formValid, state_name, error_name, idx = null) {
	if (emptyObjectValidation(idx)) {
		formValid[state_name] = true;
		if (!emptyObjectValidation(error_name)) formValid[error_name] = null;
	} else {
		formValid[idx][state_name] = true;
		if (!emptyObjectValidation(error_name)) formValid[idx][error_name] = null;
	}
};

const resetObjNewSelection = function (selection, current_value, field = "id") {
	if (emptyObjectValidation(current_value) || emptyObjectValidation(selection))
		return [];
	let val = current_value.filter(
		(c) => !emptyObjectValidation(selection.find((s) => s[field] == c)),
	);
	return val;
};

const sortList = function (items, sort_field, sort_direction = "asc") {
	return [...items].sort((a, b) => {
		// Handle null/undefined values by treating them as empty strings
		const valueA = a[sort_field] ?? "";
		const valueB = b[sort_field] ?? "";

		// Case-insensitive string comparison if values are strings
		if (typeof valueA === "string" && typeof valueB === "string") {
			const comparison = valueA.localeCompare(valueB);
			return sort_direction === "desc" ? -comparison : comparison;
		}

		// Numeric comparison
		const modifier = sort_direction === "desc" ? -1 : 1;
		return (valueA > valueB ? 1 : valueA < valueB ? -1 : 0) * modifier;
	});
};

const preventSpace = function (event) {
	const key_code = event.keyCode ? event.keyCode : event.which;
	if (event.key === " " || key_code == 32) {
		event.preventDefault();
	}
};

const cacheValidation = function (cache_selection, key) {
	if (
		emptyObjectValidation(cache_selection) ||
		emptyObjectValidation(cache_selection[key])
	) {
		return null;
	}

	// Optional: Check if cache is expired (1 hour)
	if (Date.now() - cache_selection[key].timestamp > 3600000) {
		return null;
	}

	return cache_selection[key].data;
};

const handleAlphaNumeric = function (event) {
	const key = event.key;

	if (!(key && /^[a-zA-Z0-9]$/.test(key))) {
		event.preventDefault();
	}
};

const handleResetFieldValue = function (data, selection, field = "id") {
	if (resetSelectionValue(selection, data, field)) {
		data = null;
	}

	if (emptyObjectValidation(data) && countObjLen(selection) == 1) {
		data = selection[0][field];
	}
};

const validateEmail = function (email) {
	return /^\w+([.-]?\w+)*@\w+([.-]?\w+)+$/.test(email) ? true : false;
};

const onAsciiKeyPress = function (event) {
	const key = event.key;

	// Allow control keys
	const controlKeys = [
		"Backspace",
		"Delete",
		"Tab",
		"ArrowLeft",
		"ArrowRight",
		"ArrowUp",
		"ArrowDown",
		"Home",
		"End",
		"Enter",
	];

	if (controlKeys.includes(key)) {
		return;
	}

	// Check if key is a printable ASCII character
	const asciiRegex = /^[\x20-\x7E]$/; // Space to ~ (32-126)

	if (!asciiRegex.test(key)) {
		event.preventDefault();
	}
};

const ktContentScroll = function (x, y) {
	document.querySelector("#kt_content").scrollTo(x, y - 100);
};

const calVwByM3 = function (m3, decimal_place) {
	return math.multiply(m3, 167, decimal_place);
};

const reportFooter = function (class_name, page, printed_by, total_page) {
	let footerElement = document.createElement("div");
	footerElement.className = class_name;

	let timeEle = document.createElement("div");
	timeEle.className = "time";
	timeEle.innerHTML = `Printed On: ${dateTimeUtil.UnixTimeToDateTime(new Date())}`;
	footerElement.append(timeEle);

	let pageEle = document.createElement("div");
	pageEle.className = "page";
	pageEle.innerHTML = `Page: ${page} of ${total_page}`;
	footerElement.append(pageEle);

	let printedByEle = document.createElement("div");
	printedByEle.className = "printed_by";
	printedByEle.innerHTML = `Printed By: ${printed_by}`;

	footerElement.append(printedByEle);
	return footerElement;
};

const isMobile = function (mq = null) {
	const ua = navigator.userAgent;
	const hasTouch = navigator.maxTouchPoints > 0 || "ontouchstart" in window;

	// 获取逻辑像素宽度（更准确）
	const width = window.innerWidth;
	// const height = window.innerHeight;
	const screenWidth = window.screen.width;
	// const screenHeight = window.screen.height;
	// 判断是否为平板尺寸（逻辑像素 600~1024 为平板常见区间）
	const isTabletSize =
		(width >= 600 && width < 1024) ||
		(screenWidth >= 600 && screenWidth < 1024);

	// 1. 常规手机检测
	const isMobileDevice =
		/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);

	// 2. 平板检测（重点修复 Redmi Pad SE）
	const isTablet =
		// 明确匹配 Redmi Pad 系列
		/Redmi\s*Pad|Xiaomi\s*Pad|POCO\s*Pad/i.test(ua) ||
		// 匹配通用平板关键词
		/iPad|Tablet|Touch|Surface/i.test(ua) ||
		// 核心修复：安卓 + 平板尺寸 + 触控，且品牌为小米系或分辨率特征明显
		(/Android/i.test(ua) && isTabletSize && hasTouch) ||
		// 针对 Mac 触控板误判
		(/Macintosh/i.test(ua) && hasTouch && !/Mobile/i.test(ua));

	// 3. Surface 独立检测
	const isSurface =
		/Surface/i.test(ua) || (/Touch/i.test(ua) && /Windows NT/i.test(ua));
	const isRedmiPad = /Redmi\s*Pad|Xiaomi\s*Pad|POCO\s*Pad/i.test(ua);
	const isRedmiPadSE = /Redmi/i.test(ua) && /Android/i.test(ua) && width >= 600;

	return (
		isMobileDevice ||
		isTablet ||
		isSurface ||
		isRedmiPadSE ||
		isRedmiPad ||
		(!emptyObjectValidation(Vue.prototype.$mq) &&
			(Vue.prototype.$mq === "mobile" || Vue.prototype.$mq === "tablet")) ||
		(!emptyObjectValidation(mq) && (mq === "mobile" || mq === "tablet"))
	);
};

export default {
	/**Data Formatting */
	handleTrimString,
	joinStr,
	newLineToBr,
	numberConvertExcelCol,

	/**Validation Utilities */
	copyPage,
	createPage,
	editPage,
	emptyStringValidation,
	emptyObjectValidation,
	numberValidation,
	resetSelectionValue,
	summaryPageRowActionValidation,
	viewPage,

	/**UI Utilities */
	colorIndicatorStyle,
	dialogboxHeight,
	dialogboxWidth,
	disabledButton,
	enableButton,
	failedMgsHandle,
	generateBarcode,
	handleBadgeVariant,
	handleBadgeVariantTaxRebate,
	handleDialogBoxWidth,
	handleRequiredMsg,
	handleSelectedRow,
	handleStatusDesc,
	handleYesNo,
	handleYesNoOptional,
	isDarkColor,
	placeHolderHandle,
	selectOption,
	setRequiredErrorBorder,
	successMsgHandle,
	textColor,
	resetSearchPageNum,

	/**Security Utilities */
	encryJsonPostData,
	encrypData,
	decryptData,

	/**Math Utilities */
	mathAdd: math.add,
	mathDivide: math.divide,
	mathMultiply: math.multiply,
	mathSubtract: math.subtract,
	negativeNumber,
	numberHandle,
	onlyInteger,
	onlyNumber,
	pixelToMm,
	sumArrayObj,
	roundNumber,

	/**Helper Utilities */
	contactNoFormatHandle,
	convertNestedObjectToNull,
	countObjLen,
	handleArrayLastIndex,
	handleRemoveDataTableSort,
	resetObjNewSelection,
	resetObjServer,
	resetState,
	serializeJsonString,
	soNoFullFormat,
	unserializeJsonStr,
	sortList,
	preventSpace,
	cacheValidation,
	handleAlphaNumeric,
	handleResetFieldValue,
	validateEmail,
	onAsciiKeyPress,
	ktContentScroll,

	calVwByM3,
	reportFooter,
	isMobile,

	...dateTimeUtil,
	...helper,
	...selection,
	...dataFormat,
};
